/**
 * The governed final-fare revision (W8-FINAL-FARE-AND-CASH, D438, ADR-0070; owner answer D435 §5.0 item 2; finding G-02).
 *
 * The price is fixed at the quote. Once the passenger has boarded, when the EXECUTED trip diverges from the QUOTED route
 * the final fare is recomputed — up or down — under `fare.finalRevision`, a settings-kernel key (D393, ADR-0060: four
 * eyes on every change, versioned, effective-dated, content-hashed; the country pack's seed publishes the first version
 * through the governed seed path). Per country and market, with a rule per service category (else the default rule):
 *
 *  - the DEVIATION thresholds that trigger a revision: distance and duration, each as a percentage (basis points) AND an
 *    absolute amount — a dimension triggers only when it deviates by more than BOTH (so a 300 m detour on a 1 km trip
 *    and a 2 % miss on a 40 km trip both stay as quoted); either dimension triggering revises the fare;
 *  - which DIRECTIONS apply (`up`, `down`), each separately;
 *  - the CAP (most the final may exceed the quote) and the FLOOR (most it may fall below it), in basis points of the quote;
 *  - the QUOTE MATCH tolerance (review finding W8G-05): how far a ride request's (or desk order's) pickup and destination
 *    may sit from the points the quote priced — beyond it the request is refused, so a quote is never a voucher for
 *    another trip (an ungoverned market uses {@link DEFAULT_QUOTE_MATCH_METERS});
 *  - the EVIDENCE bar of the executed distance: at least `minSamples` accepted location samples and no gap longer than
 *    `maxGapSeconds` between boarding, the samples and the completion (a thin trace under-measures the distance and
 *    would revise DOWN on missing data — such a trace is never used; the duration comes from the ride's own timeline).
 *
 * D855 (owner Q3, 2026-10-30) — fully parametrizable, all OPTIONAL (a version without them decides exactly as before):
 *  - per DIRECTION (`revise.upward` / `revise.downward`): the BASIS (distance, time, both, either), the bars (a percentage
 *    over/under the quoted value and/or an absolute amount) and WHAT IS CHARGED once it fires (the whole difference, only the
 *    dimensions that fired, or only the part beyond the bar); `revise.down: false` = "never charge less, only more";
 *  - AMOUNT bounds (`revise.maxIncrease` / `maxDecrease`, minor units + currency) beside the percentage cap/floor;
 *  - without distance evidence (`evidence.withoutDistance`): revise on time alone (the default) or keep the quote.
 * Every decision records WHICH trigger fired (`triggerCode`) and WHICH bound applied (`capCode`) beside its reason code.
 *
 * A market version may refine its country's, but its money bounds are NARROW-ONLY (AGENTS: a lower scope never loosens a
 * country-level financial constraint): for the rule that applies, the cap and the floor are the SMALLER of the market's
 * and the country's, and a direction applies only when both allow it.
 *
 * The revision itself reprices the executed trip with the quote's own pricing rule and moves the quoted total by the
 * DIFFERENCE between the rule's price for the executed and for the quoted distance/duration — the quote's discount,
 * fees and minimum stay what the rider accepted — then bounds it by the cap and the floor. The route plausibility guard
 * of D436 (`@vello/geo-providers` `isPlausibleRoute`, the donor's 0037 guard) is applied by the caller to the executed
 * distance; an implausible trace is not used.
 *
 * Fail safe: no governing version, an unverifiable one, a ride without the D436 route snapshot, a ride with no boarding
 * instant, or a pricing rule that is not the quote's — the final fare is the QUOTED fare, with a reason code saying why
 * (the pre-D438 behaviour). Money is integer minor units in the quote's currency. Neutral: no country or currency.
 */
import { money } from '@vello/core';
import { SCOPE_REF_KIND, marketSettingContext, policySetting, resolvePolicy, settingScopeRefIn, settingScopeRefValid, settingSnapshotFields } from '@vello/config';
import { haversineMeters } from '@vello/geo';
import { agreedPriceInForce } from '@vello/ride';
import { quoteFare } from './quote.js';
import { priceWait } from './rules.js';
export const FINAL_FARE_POLICY_KEY = 'fare.finalRevision';
/**
 * D855 (owner Q3, 2026-10-30): which dimensions decide that a direction applies. `DISTANCE` / `DURATION` — that one alone;
 * `BOTH` — distance AND time must both pass their bar; `EITHER` — distance OR time.
 */
export const FINAL_FARE_BASES = ['DISTANCE', 'DURATION', 'BOTH', 'EITHER'];
/**
 * D855: what a direction charges once it applies. `FULL_DIFFERENCE` — the whole executed trip is repriced (distance and time
 * as driven); `TRIGGERED_ONLY` — only the dimensions that passed their bar are repriced, the others stay as quoted;
 * `BEYOND_THRESHOLD` — only the part of each passing dimension BEYOND its bar is repriced (the bar works as a tolerance).
 */
export const FINAL_FARE_CHARGES = ['FULL_DIFFERENCE', 'TRIGGERED_ONLY', 'BEYOND_THRESHOLD'];
/** D855 (owner Q3): what the revision does when the distance is not evidenced (a thin, gappy or implausible trace). */
export const FINAL_FARE_WITHOUT_DISTANCE = ['TIME_ONLY', 'AS_QUOTED'];
export const FINAL_FARE_POLICY_REASON_CODES = ['FINAL_FARE_POLICY_GOVERNED', 'FINAL_FARE_POLICY_UNSET', 'FINAL_FARE_POLICY_INVALID'];
/** The stable reason codes a final-fare decision carries. */
export const FINAL_FARE_REASON_CODES = [
    'FINAL_FARE_AS_QUOTED',
    'FINAL_FARE_REVISED_UP',
    'FINAL_FARE_REVISED_UP_CAPPED',
    'FINAL_FARE_REVISED_DOWN',
    'FINAL_FARE_REVISED_DOWN_FLOORED',
    'FINAL_FARE_DIRECTION_DISABLED',
    'FINAL_FARE_POLICY_UNSET',
    'FINAL_FARE_POLICY_INVALID',
    'FINAL_FARE_NO_QUOTED_ROUTE',
    'FINAL_FARE_NO_BOARDING',
    'FINAL_FARE_PRICING_UNAVAILABLE',
    // D438 review (W8F-02): a partner-billed ride (payer ACCOUNT) keeps the price its account authorized at the quote.
    'FINAL_FARE_ACCOUNT_BILLED_AT_QUOTE',
    // D465b: the ride carries a price the rider and the driver agreed — it is the fixed price (no route revision), plus only D463's wait line.
    'FINAL_FARE_NEGOTIATED',
];
/**
 * D855: WHICH trigger fired, recorded as `triggerCode` beside the reason code on every decision that reached the thresholds.
 * `..._NO_DISTANCE_EVIDENCE`: it fired on time alone because the distance was not evidenced (`withoutDistance: TIME_ONLY`);
 * `FINAL_FARE_NO_DISTANCE_EVIDENCE`: the distance was not evidenced and the rule keeps the quote then (`AS_QUOTED`).
 */
export const FINAL_FARE_TRIGGER_CODES = [
    'FINAL_FARE_TRIGGER_NONE',
    'FINAL_FARE_TRIGGER_DISTANCE',
    'FINAL_FARE_TRIGGER_DURATION',
    'FINAL_FARE_TRIGGER_DISTANCE_AND_DURATION',
    'FINAL_FARE_TRIGGER_DURATION_NO_DISTANCE_EVIDENCE',
    'FINAL_FARE_NO_DISTANCE_EVIDENCE',
];
/** D855: WHICH bound stopped a revision, recorded as `capCode`: the percentage or the amount, up (cap) or down (floor). */
export const FINAL_FARE_CAP_CODES = ['FINAL_FARE_CAP_PERCENT', 'FINAL_FARE_CAP_AMOUNT', 'FINAL_FARE_FLOOR_PERCENT', 'FINAL_FARE_FLOOR_AMOUNT'];
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CATEGORY = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const intIn = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const exactKeys = (o, keys) => keys.every((k) => k in o) && Object.keys(o).every((k) => keys.includes(k));
/** Every required member present, and nothing outside the required and optional members (D855: the optional members). */
const knownKeys = (o, required, optional) => required.every((k) => k in o) && Object.keys(o).every((k) => required.includes(k) || optional.includes(k));
const CURRENCY = /^[A-Z]{3}$/;
/** The largest amount a revision bound may hold (a safe integer far above any fare). */
const MAX_REVISION_AMOUNT_MINOR = 1_000_000_000_000;
/** D855: a dimension bar — known members only, at least one, each an integer in range. */
function validBar(b, absolute, maxAbsolute) {
    if (!isObj(b) || !knownKeys(b, [], ['basisPoints', absolute]) || Object.keys(b).length === 0)
        return false;
    if ('basisPoints' in b && !intIn(b.basisPoints, 0, 100_000))
        return false;
    if (absolute in b && !intIn(b[absolute], 0, maxAbsolute))
        return false;
    return true;
}
function validDirectionRule(d) {
    if (!isObj(d) || !knownKeys(d, ['basis'], ['distance', 'duration', 'charge']))
        return false;
    if (!FINAL_FARE_BASES.includes(d.basis))
        return false;
    if ('distance' in d && !validBar(d.distance, 'meters', 1_000_000))
        return false;
    if ('duration' in d && !validBar(d.duration, 'seconds', 86_400))
        return false;
    if ('charge' in d && !FINAL_FARE_CHARGES.includes(d.charge))
        return false;
    return true;
}
function validAmount(a) {
    return isObj(a) && exactKeys(a, ['amountMinor', 'currency']) && intIn(a.amountMinor, 0, MAX_REVISION_AMOUNT_MINOR) && typeof a.currency === 'string' && CURRENCY.test(a.currency);
}
/** The stable issue of a rule that is not valid, or undefined. */
export function finalFareRuleIssue(r) {
    if (!isObj(r) || !exactKeys(r, ['distance', 'duration', 'revise', 'capBasisPoints', 'floorBasisPoints', 'evidence', 'quoteMatch']))
        return 'INVALID_RULE';
    const d = r.distance;
    if (!isObj(d) || !exactKeys(d, ['thresholdBasisPoints', 'thresholdMeters']) || !intIn(d.thresholdBasisPoints, 0, 100_000) || !intIn(d.thresholdMeters, 0, 1_000_000))
        return 'INVALID_DISTANCE_THRESHOLD';
    const t = r.duration;
    if (!isObj(t) || !exactKeys(t, ['thresholdBasisPoints', 'thresholdSeconds']) || !intIn(t.thresholdBasisPoints, 0, 100_000) || !intIn(t.thresholdSeconds, 0, 86_400))
        return 'INVALID_DURATION_THRESHOLD';
    const v = r.revise;
    if (!isObj(v) || !knownKeys(v, ['up', 'down'], ['upward', 'downward', 'maxIncrease', 'maxDecrease']) || typeof v.up !== 'boolean' || typeof v.down !== 'boolean')
        return 'INVALID_DIRECTIONS';
    if ('upward' in v && !validDirectionRule(v.upward))
        return 'INVALID_UPWARD_TRIGGER';
    if ('downward' in v && !validDirectionRule(v.downward))
        return 'INVALID_DOWNWARD_TRIGGER';
    if ('maxIncrease' in v && !validAmount(v.maxIncrease))
        return 'INVALID_MAX_INCREASE';
    if ('maxDecrease' in v && !validAmount(v.maxDecrease))
        return 'INVALID_MAX_DECREASE';
    if (isObj(v.maxIncrease) && isObj(v.maxDecrease) && v.maxIncrease.currency !== v.maxDecrease.currency)
        return 'INVALID_REVISION_CURRENCY';
    if (!intIn(r.capBasisPoints, 0, 10_000))
        return 'INVALID_CAP';
    if (!intIn(r.floorBasisPoints, 0, 10_000))
        return 'INVALID_FLOOR';
    const e = r.evidence;
    if (!isObj(e) || !knownKeys(e, ['minSamples', 'maxGapSeconds'], ['withoutDistance']) || !intIn(e.minSamples, 2, 10_000) || !intIn(e.maxGapSeconds, 5, 3_600))
        return 'INVALID_EVIDENCE';
    if ('withoutDistance' in e && !FINAL_FARE_WITHOUT_DISTANCE.includes(e.withoutDistance))
        return 'INVALID_EVIDENCE';
    const q = r.quoteMatch;
    if (!isObj(q) || !exactKeys(q, ['maxEndpointOffsetMeters']) || !intIn(q.maxEndpointOffsetMeters, 0, 50_000))
        return 'INVALID_QUOTE_MATCH';
    return undefined;
}
/** The stable issue of a value that is not a final-fare policy, or undefined when it is one. */
export function finalFarePolicyIssue(v) {
    if (!isObj(v) || !exactKeys(v, ['default', 'byCategory']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const d = finalFareRuleIssue(v.default);
    if (d !== undefined)
        return d;
    if (!isObj(v.byCategory))
        return 'INVALID_CATEGORIES';
    for (const [k, rule] of Object.entries(v.byCategory)) {
        if (!CATEGORY.test(k))
            return 'INVALID_CATEGORIES';
        const issue = finalFareRuleIssue(rule);
        if (issue !== undefined)
            return issue;
    }
    return undefined;
}
export function validFinalFarePolicyValue(v) {
    return finalFarePolicyIssue(v) === undefined;
}
/** The normalized value a version stores (category keys sorted, so the approved bytes are the governing bytes). */
export function normalizeFinalFarePolicyValue(v) {
    const byCategory = {};
    for (const k of Object.keys(v.byCategory).sort())
        byCategory[k] = v.byCategory[k];
    return { default: v.default, byCategory };
}
/** `fare.finalRevision@<scope>:<ref>#v<n>`. */
export function finalFareVersionOf(scope, scopeRef, version) {
    return `${FINAL_FARE_POLICY_KEY}@${scope}:${scopeRef}#v${version}`;
}
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function finalFarePolicySetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope final-fare policy needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = finalFarePolicyIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid final-fare policy: ${issue}`);
    return policySetting({
        key: FINAL_FARE_POLICY_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: normalizeFinalFarePolicyValue(input.value),
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/** D855: the tighter of two amount bounds (one alone binds; none → undefined; two currencies → null, not comparable). */
function smallerAmount(a, b) {
    if (a === undefined)
        return b;
    if (b === undefined)
        return a;
    if (a.currency !== b.currency)
        return null;
    return a.amountMinor <= b.amountMinor ? a : b;
}
/** The rule of a category under a policy value (its own, else the default). */
export function finalFareRuleFor(value, categoryCode) {
    return value.byCategory[categoryCode] ?? value.default;
}
/**
 * The rule in force for `(marketId, categoryCode)` at `at`, over the GOVERNED reader's versions. A market version
 * refines its country's, except the money band: the cap and the floor are each the smaller of the two, and a direction
 * applies only when both allow it. The same holds for a state version (ADR-0124: `regionCode` is the market's registered state, none skips the
 * layer; the most specific of the state and the market decides, narrow-only against the country).
 */
export function resolveFinalFarePolicy(settings, marketId, categoryCode, at, regionCode) {
    if (!MARKET_ID.test(marketId))
        return { governed: false, reasonCode: 'FINAL_FARE_POLICY_INVALID' };
    const ctx = marketSettingContext(marketId, regionCode);
    const country = ctx.country;
    const relevant = settings.filter((s) => s.key === FINAL_FARE_POLICY_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    const resolution = resolvePolicy(relevant, ctx, FINAL_FARE_POLICY_KEY, at);
    if (resolution.reason === 'NOT_SET')
        return { governed: false, reasonCode: 'FINAL_FARE_POLICY_UNSET' };
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market')) {
        return { governed: false, reasonCode: 'FINAL_FARE_POLICY_INVALID' };
    }
    const ref = settingScopeRefIn(resolution.scope, ctx);
    const policyVersion = finalFareVersionOf(resolution.scope, ref, resolution.version);
    if (!validFinalFarePolicyValue(resolution.value))
        return { governed: false, reasonCode: 'FINAL_FARE_POLICY_INVALID', policyVersion };
    let rule = finalFareRuleFor(resolution.value, categoryCode);
    let narrowedToCountry = false;
    let countryBound;
    if (resolution.scope !== 'country') {
        const countryOnly = resolvePolicy(relevant.filter((s) => s.scope === 'country'), { country }, FINAL_FARE_POLICY_KEY, at);
        if (countryOnly.reason !== 'NOT_SET') {
            // A country version the reader cannot verify fails the whole decision closed (the market cannot vouch for it).
            if (!validFinalFarePolicyValue(countryOnly.value) || countryOnly.version === undefined)
                return { governed: false, reasonCode: 'FINAL_FARE_POLICY_INVALID', policyVersion };
            const c = finalFareRuleFor(countryOnly.value, categoryCode);
            // D855: the amount bounds narrow like the percentages (the smaller of the two; one set alone binds); two currencies cannot be compared — fail closed.
            const maxIncrease = smallerAmount(rule.revise.maxIncrease, c.revise.maxIncrease);
            const maxDecrease = smallerAmount(rule.revise.maxDecrease, c.revise.maxDecrease);
            if (maxIncrease === null || maxDecrease === null)
                return { governed: false, reasonCode: 'FINAL_FARE_POLICY_INVALID', policyVersion };
            // D855: keeping the quote without distance evidence is the stricter choice — a lower level cannot loosen it to a time-only revision.
            const withoutDistance = rule.evidence.withoutDistance === 'AS_QUOTED' || c.evidence.withoutDistance === 'AS_QUOTED' ? 'AS_QUOTED' : rule.evidence.withoutDistance;
            const narrowed = {
                ...rule,
                revise: {
                    ...rule.revise,
                    up: rule.revise.up && c.revise.up,
                    down: rule.revise.down && c.revise.down,
                    ...(maxIncrease !== undefined ? { maxIncrease } : {}),
                    ...(maxDecrease !== undefined ? { maxDecrease } : {}),
                },
                capBasisPoints: Math.min(rule.capBasisPoints, c.capBasisPoints),
                floorBasisPoints: Math.min(rule.floorBasisPoints, c.floorBasisPoints),
                // D438 review (W8F-05): the evidence a distance revision rests on is a money bound too — a market may demand
                // MORE samples and a SHORTER gap than its country, never fewer or longer.
                evidence: {
                    minSamples: Math.max(rule.evidence.minSamples, c.evidence.minSamples),
                    maxGapSeconds: Math.min(rule.evidence.maxGapSeconds, c.evidence.maxGapSeconds),
                    ...(withoutDistance !== undefined ? { withoutDistance } : {}),
                },
                quoteMatch: { maxEndpointOffsetMeters: Math.min(rule.quoteMatch.maxEndpointOffsetMeters, c.quoteMatch.maxEndpointOffsetMeters) },
            };
            narrowedToCountry = JSON.stringify(narrowed) !== JSON.stringify(rule);
            rule = narrowed;
            // The country version bounds this decision: it is snapshotted with it (W8F-05).
            const countrySnap = settingSnapshotFields(relevant, FINAL_FARE_POLICY_KEY, 'country', country, countryOnly.version);
            countryBound = { version: finalFareVersionOf('country', country, countryOnly.version), ...(countrySnap.contentHash !== undefined ? { contentHash: countrySnap.contentHash } : {}) };
        }
    }
    const snap = settingSnapshotFields(relevant, FINAL_FARE_POLICY_KEY, resolution.scope, ref, resolution.version);
    return {
        governed: true,
        reasonCode: 'FINAL_FARE_POLICY_GOVERNED',
        rule,
        policyVersion,
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
        ...(narrowedToCountry ? { narrowedToCountry: true } : {}),
        ...(countryBound !== undefined ? { countryBound } : {}),
    };
}
// ---- W8G-05: a request must be the trip its quote priced -----------------------------------------------------------
/** The tolerance an ungoverned market applies (conservative; a governed version replaces it). */
export const DEFAULT_QUOTE_MATCH_METERS = 500;
export const QUOTE_MATCH_REFUSAL = 'REQUEST_STOPS_DO_NOT_MATCH_QUOTE';
/** The tolerance a policy decision implies (the default when no version governs, or it cannot be verified). */
export function quoteMatchTolerance(decision) {
    return decision.governed
        ? { maxEndpointOffsetMeters: decision.rule.quoteMatch.maxEndpointOffsetMeters, reasonCode: 'QUOTE_MATCH_GOVERNED', policyVersion: decision.policyVersion }
        : { maxEndpointOffsetMeters: DEFAULT_QUOTE_MATCH_METERS, reasonCode: 'QUOTE_MATCH_DEFAULT' };
}
/**
 * Whether a request's pickup and destination are the trip its quote priced (within the tolerance). A quote issued before
 * D438 carries no endpoints and is not checked (`checked: false`; quotes live minutes). A request missing its pickup or
 * destination stop is refused (it cannot be the quoted trip).
 */
export function checkStopsMatchQuote(endpoints, stops, tolerance) {
    if (endpoints === undefined)
        return { ok: true, checked: false };
    const pickup = stops.find((s) => s.type === 'pickup')?.location;
    const destination = stops.find((s) => s.type === 'destination')?.location;
    const offset = (a, b) => a === undefined || !Number.isFinite(a.latitude) || !Number.isFinite(a.longitude) ? undefined : Math.round(Number(haversineMeters(a, b)));
    const p = offset(pickup, endpoints.origin);
    const d = offset(destination, endpoints.destination);
    const offending = [];
    if (p === undefined || p > tolerance.maxEndpointOffsetMeters)
        offending.push('pickup');
    if (d === undefined || d > tolerance.maxEndpointOffsetMeters)
        offending.push('destination');
    if (offending.length === 0)
        return { ok: true, checked: true };
    return { ok: false, reasonCode: QUOTE_MATCH_REFUSAL, offending, offsetsMeters: { ...(p !== undefined ? { pickup: p } : {}), ...(d !== undefined ? { destination: d } : {}) } };
}
/**
 * Measure a trace between boarding and completion: samples outside the window are ignored, the rest are ordered by time.
 * The gap measure includes the lead-in (boarding → first sample) and the tail (last sample → completion), so a trace
 * that starts late or stops early does not evidence the whole trip.
 */
export function summarizeExecutedTrace(samples, boardedAtMs, completedAtMs) {
    const inside = samples.filter((s) => s.recordedAtMs >= boardedAtMs && s.recordedAtMs <= completedAtMs).sort((a, b) => a.recordedAtMs - b.recordedAtMs);
    if (inside.length === 0)
        return { sampleCount: 0, distanceMeters: 0, maxGapSeconds: Math.max(0, Math.round((completedAtMs - boardedAtMs) / 1000)) };
    let meters = 0;
    let maxGapMs = Math.max(inside[0].recordedAtMs - boardedAtMs, completedAtMs - inside[inside.length - 1].recordedAtMs);
    for (let i = 1; i < inside.length; i++) {
        const a = inside[i - 1];
        const b = inside[i];
        meters += Number(haversineMeters(a.position, b.position));
        maxGapMs = Math.max(maxGapMs, b.recordedAtMs - a.recordedAtMs);
    }
    return { sampleCount: inside.length, distanceMeters: Math.round(meters), maxGapSeconds: Math.round(maxGapMs / 1000) };
}
const bp = (amount, basisPoints) => (amount * BigInt(basisPoints)) / 10000n;
/**
 * Decide the final fare of a completed ride. Pure and deterministic: the governed route revision (D438), then the measured
 * wait (D463) — the wait charge is added to whatever the revision decided, so a ride on hold is charged what its quote's
 * terms say even in a market with no `fare.finalRevision` policy.
 */
export function decideFinalFare(input) {
    const revised = decideRevision(input);
    // D689: the wait at the pickup counts only under wait terms the quote carries (a tariff that charges no wait records nothing new for a rider who was merely late).
    const pickupSeconds = input.quote.waitTerms !== undefined && Number.isFinite(input.pickupWaitSeconds) && (input.pickupWaitSeconds ?? 0) > 0 ? Math.floor(input.pickupWaitSeconds) : 0;
    const wait = priceWait(input.quote.waitTerms, (input.heldSeconds ?? 0) + pickupSeconds);
    if (wait.reasonCode === 'WAIT_NONE')
        return revised;
    const currency = revised.final.currency;
    // D463 review (W9B-01): a partner-billed ride settles its account charge at the quote and never above it (D438 W8F-02) —
    // the held time is recorded, with its own reason code, and not charged.
    if (input.billedAtQuote === true) {
        const zero = money(0n, currency);
        return {
            ...revised,
            waiting: { reasonCode: 'WAIT_ACCOUNT_BILLED_AT_QUOTE', measuredSeconds: wait.measuredSeconds, ...(pickupSeconds > 0 ? { pickupSeconds } : {}), chargedSeconds: 0, perMinute: zero, penalty: zero, charge: zero, ...(input.quote.waitTerms !== undefined ? { pricingVersion: input.quote.waitTerms.pricingVersion } : {}) },
        };
    }
    const waiting = {
        reasonCode: wait.reasonCode,
        measuredSeconds: wait.measuredSeconds,
        ...(pickupSeconds > 0 ? { pickupSeconds } : {}),
        chargedSeconds: wait.chargedSeconds,
        perMinute: money(wait.perMinuteMinor, currency),
        penalty: money(wait.penaltyMinor, currency),
        charge: money(wait.totalMinor, currency),
        ...(input.quote.waitTerms !== undefined ? { pricingVersion: input.quote.waitTerms.pricingVersion } : {}),
    };
    if (wait.totalMinor === 0n)
        return { ...revised, waiting };
    return { ...revised, final: money(revised.final.amountMinor + wait.totalMinor, currency), waiting };
}
function decideRevision(input) {
    const quoted = input.quote.total;
    if (input.agreedPrice !== undefined) {
        const route = input.quote.route;
        const boardedMs = input.boardedAt !== undefined ? Date.parse(input.boardedAt) : Number.NaN;
        const completedMs = Date.parse(input.completedAt);
        const durationSeconds = Number.isFinite(boardedMs) && Number.isFinite(completedMs) ? Math.max(0, Math.round((completedMs - boardedMs) / 1000)) : undefined;
        return {
            quoted,
            final: input.agreedPrice,
            direction: 'NONE',
            reasonCode: 'FINAL_FARE_NEGOTIATED',
            ...(route !== undefined ? { quotedDistanceMeters: route.distanceMeters, quotedDurationSeconds: route.durationSeconds } : {}),
            executed: { ...(durationSeconds !== undefined ? { durationSeconds } : {}), distanceEvidence: 'UNAVAILABLE', sampleCount: 0 },
            decidedAt: input.completedAt,
        };
    }
    const route = input.quote.route;
    const boardedMs = input.boardedAt !== undefined ? Date.parse(input.boardedAt) : Number.NaN;
    const completedMs = Date.parse(input.completedAt);
    const durationSeconds = Number.isFinite(boardedMs) && Number.isFinite(completedMs) ? Math.max(0, Math.round((completedMs - boardedMs) / 1000)) : undefined;
    const policy = input.policy.governed
        ? {
            key: FINAL_FARE_POLICY_KEY, version: input.policy.policyVersion,
            ...(input.policy.contentHash !== undefined ? { contentHash: input.policy.contentHash } : {}),
            ...(input.policy.changeId !== undefined ? { changeId: input.policy.changeId } : {}),
            ...(input.policy.countryBound !== undefined ? { countryBound: input.policy.countryBound } : {}),
        }
        : input.policy.policyVersion !== undefined ? { key: FINAL_FARE_POLICY_KEY, version: input.policy.policyVersion } : undefined;
    const trace = input.trace;
    let distanceEvidence = 'UNAVAILABLE';
    if (trace !== undefined && input.policy.governed) {
        const ev = input.policy.rule.evidence;
        if (trace.sampleCount < ev.minSamples || trace.maxGapSeconds > ev.maxGapSeconds)
            distanceEvidence = 'INSUFFICIENT';
        else if (route !== undefined && durationSeconds !== undefined && input.plausible !== undefined && !input.plausible(trace.distanceMeters, durationSeconds, route.straightLineMeters))
            distanceEvidence = 'IMPLAUSIBLE';
        else
            distanceEvidence = 'SAMPLES';
    }
    const executedDistance = distanceEvidence === 'SAMPLES' && trace !== undefined ? trace.distanceMeters : undefined;
    const executed = {
        ...(durationSeconds !== undefined ? { durationSeconds } : {}),
        ...(executedDistance !== undefined ? { distanceMeters: executedDistance } : {}),
        distanceEvidence,
        sampleCount: trace?.sampleCount ?? 0,
    };
    const base = {
        quoted,
        ...(policy !== undefined ? { policy } : {}),
        ...(route !== undefined ? { quotedDistanceMeters: route.distanceMeters, quotedDurationSeconds: route.durationSeconds } : {}),
        executed,
        decidedAt: input.completedAt,
    };
    const asQuoted = (reasonCode, extra = {}) => ({ ...base, final: quoted, direction: 'NONE', reasonCode, ...extra });
    if (!input.policy.governed)
        return asQuoted(input.policy.reasonCode);
    if (input.billedAtQuote === true)
        return asQuoted('FINAL_FARE_ACCOUNT_BILLED_AT_QUOTE');
    if (route === undefined)
        return asQuoted('FINAL_FARE_NO_QUOTED_ROUTE');
    if (durationSeconds === undefined)
        return asQuoted('FINAL_FARE_NO_BOARDING');
    const rule = input.policy.rule;
    // D855: an amount bound in another currency than the quote's cannot be applied — the quote stands (fail safe).
    if ((rule.revise.maxIncrease !== undefined && rule.revise.maxIncrease.currency !== quoted.currency) || (rule.revise.maxDecrease !== undefined && rule.revise.maxDecrease.currency !== quoted.currency)) {
        return asQuoted('FINAL_FARE_POLICY_INVALID');
    }
    const pricing = input.pricingRule;
    const pricingUsable = pricing !== undefined && pricing.pricingVersion === input.quote.pricingVersion && pricing.currency === quoted.currency;
    // D464: the quote's own time band and surge multiplier price BOTH sides of the revision — the delta is what the quote
    // would have charged for the executed trip, never a neutral-price delta applied to a surged fare.
    const quotedModifiers = { ...(input.quote.surgeMultiplierBasisPoints !== undefined ? { surgeMultiplierBasisPoints: input.quote.surgeMultiplierBasisPoints } : {}), ...(input.quote.timeBand !== undefined ? { timeBand: input.quote.timeBand } : {}) };
    const priceAt = (distanceMeters, seconds) => quoteFare(pricing, { quoteId: 'final-fare', createdAt: input.completedAt, distanceMeters, durationSeconds: seconds, ...(input.quote.destinationMarketId !== undefined ? { destinationMarketId: input.quote.destinationMarketId } : {}), ...quotedModifiers }).total.amountMinor;
    const outcome = reviseRoute(rule, {
        quotedMinor: quoted.amountMinor,
        quotedDistanceMeters: route.distanceMeters,
        quotedDurationSeconds: route.durationSeconds,
        ...(executedDistance !== undefined ? { executedDistanceMeters: executedDistance } : {}),
        executedDurationSeconds: durationSeconds,
    }, pricingUsable ? priceAt : undefined);
    const detail = {
        ...(outcome.triggered !== undefined ? { triggered: outcome.triggered } : {}),
        triggerCode: outcome.triggerCode,
        ...(outcome.priced && pricing !== undefined ? { pricingVersion: pricing.pricingVersion } : {}),
        ...(outcome.capCode !== undefined ? { capCode: outcome.capCode } : {}),
    };
    if (outcome.direction === 'NONE')
        return asQuoted(outcome.reasonCode, detail);
    return { ...base, ...detail, final: money(outcome.finalMinor, quoted.currency), direction: outcome.direction, reasonCode: outcome.reasonCode };
}
/** How far a dimension moved in a sense (+1: over the quote, -1: under it); never negative. */
const moved = (quoted, executed, sense) => Math.max(0, (executed - quoted) * sense);
/** Whether a movement passes a bar: every bar member present must be passed (the general threshold: both, as since D438). */
function passes(quoted, movement, basisPoints, absolute) {
    if (movement <= 0)
        return false;
    if (basisPoints !== undefined && !(movement * 10_000 > quoted * basisPoints))
        return false;
    if (absolute !== undefined && !(movement > absolute))
        return false;
    return true;
}
/** The tolerance a bar grants (the largest of its members): BEYOND_THRESHOLD charges only what lies past it. */
const tolerance = (quoted, basisPoints, absolute) => Math.max(basisPoints !== undefined ? (quoted * basisPoints) / 10_000 : 0, absolute ?? 0);
/** A direction's trigger with every gap filled from the rule's general thresholds. */
function directionOf(rule, own) {
    const generalDistance = { basisPoints: rule.distance.thresholdBasisPoints, absolute: rule.distance.thresholdMeters };
    const generalDuration = { basisPoints: rule.duration.thresholdBasisPoints, absolute: rule.duration.thresholdSeconds };
    if (own === undefined)
        return { basis: 'EITHER', distance: generalDistance, duration: generalDuration, charge: 'FULL_DIFFERENCE' };
    return {
        basis: own.basis,
        distance: own.distance !== undefined ? { ...(own.distance.basisPoints !== undefined ? { basisPoints: own.distance.basisPoints } : {}), ...(own.distance.meters !== undefined ? { absolute: own.distance.meters } : {}) } : generalDistance,
        duration: own.duration !== undefined ? { ...(own.duration.basisPoints !== undefined ? { basisPoints: own.duration.basisPoints } : {}), ...(own.duration.seconds !== undefined ? { absolute: own.duration.seconds } : {}) } : generalDuration,
        charge: own.charge ?? 'FULL_DIFFERENCE',
    };
}
/** Which dimensions a basis makes count, given which passed (no distance evidence: distance never passes; BOTH falls to time when time-only is allowed). */
function firedBy(basis, passed, distanceKnown) {
    switch (basis) {
        case 'DISTANCE': return passed.distance ? { distance: true, duration: false } : undefined;
        case 'DURATION': return passed.duration ? { distance: false, duration: true } : undefined;
        case 'BOTH':
            if (!distanceKnown)
                return passed.duration ? { distance: false, duration: true } : undefined;
            return passed.distance && passed.duration ? { distance: true, duration: true } : undefined;
        case 'EITHER': return passed.distance || passed.duration ? passed : undefined;
    }
}
function triggerCodeOf(fired, distanceKnown) {
    if (fired.distance && fired.duration)
        return 'FINAL_FARE_TRIGGER_DISTANCE_AND_DURATION';
    if (fired.distance)
        return 'FINAL_FARE_TRIGGER_DISTANCE';
    if (fired.duration)
        return distanceKnown ? 'FINAL_FARE_TRIGGER_DURATION' : 'FINAL_FARE_TRIGGER_DURATION_NO_DISTANCE_EVIDENCE';
    return 'FINAL_FARE_TRIGGER_NONE';
}
/**
 * The governed route revision (D438, D855). Pure: `priceAt` prices a (distance, duration) under the quote's own rule (absent →
 * the pricing is unavailable and the quote stands once a trigger fires). Money in minor units of the quote's currency.
 *
 * Without `revise.upward`/`downward` it decides exactly as since D438: either dimension past the general threshold, in either
 * sense, reprices the executed trip and the price difference decides the direction. With them, an increase needs the upward
 * trigger and a decrease the downward one (a direction that is switched off never fires); when both fire, the whole trip is
 * repriced and the difference decides. Then the cap/floor: the percentage and, when set, the amount — the tighter applies.
 */
export function reviseRoute(rule, f, priceAt) {
    const quoted = f.quotedMinor;
    const keep = (reasonCode, triggerCode, extra = {}) => ({
        finalMinor: quoted, direction: 'NONE', reasonCode, triggerCode, priced: extra.priced ?? false,
        ...(extra.triggered !== undefined ? { triggered: extra.triggered } : {}),
        ...(extra.capCode !== undefined ? { capCode: extra.capCode } : {}),
    });
    const distanceKnown = f.executedDistanceMeters !== undefined;
    // D855 (owner Q3): no distance evidence and the rule keeps the quote then.
    if (!distanceKnown && rule.evidence.withoutDistance === 'AS_QUOTED')
        return keep('FINAL_FARE_AS_QUOTED', 'FINAL_FARE_NO_DISTANCE_EVIDENCE');
    const qd = f.quotedDistanceMeters;
    const qt = f.quotedDurationSeconds;
    const ed = f.executedDistanceMeters ?? qd;
    const et = f.executedDurationSeconds;
    const directional = rule.revise.upward !== undefined || rule.revise.downward !== undefined;
    const up = directionOf(rule, rule.revise.upward);
    const down = directionOf(rule, rule.revise.downward);
    const passedIn = (d, sense) => ({
        distance: distanceKnown && passes(qd, moved(qd, ed, sense), d.distance.basisPoints, d.distance.absolute),
        duration: passes(qt, moved(qt, et, sense), d.duration.basisPoints, d.duration.absolute),
    });
    const passedUp = passedIn(up, 1);
    const passedDown = passedIn(down, -1);
    const triggered = { distance: passedUp.distance || passedDown.distance, duration: passedUp.duration || passedDown.duration };
    // What to reprice, and in which direction (undefined: the price difference decides).
    let point;
    let fixed;
    let triggerCode;
    if (!directional) {
        // Since D438: a dimension counts when it moved past the general threshold in EITHER sense.
        if (!triggered.distance && !triggered.duration)
            return keep('FINAL_FARE_AS_QUOTED', 'FINAL_FARE_TRIGGER_NONE', { triggered });
        point = { distance: ed, duration: et };
        fixed = undefined;
        triggerCode = triggerCodeOf(triggered, distanceKnown);
    }
    else {
        const firedUp = firedBy(up.basis, passedUp, distanceKnown);
        const firedDown = firedBy(down.basis, passedDown, distanceKnown);
        const activeUp = firedUp !== undefined && rule.revise.up ? firedUp : undefined;
        const activeDown = firedDown !== undefined && rule.revise.down ? firedDown : undefined;
        if (firedUp === undefined && firedDown === undefined)
            return keep('FINAL_FARE_AS_QUOTED', 'FINAL_FARE_TRIGGER_NONE', { triggered });
        if (activeUp === undefined && activeDown === undefined) {
            return keep('FINAL_FARE_DIRECTION_DISABLED', triggerCodeOf({ distance: (firedUp?.distance ?? false) || (firedDown?.distance ?? false), duration: (firedUp?.duration ?? false) || (firedDown?.duration ?? false) }, distanceKnown), { triggered });
        }
        if (activeUp !== undefined && activeDown !== undefined) {
            // Longer but faster (or shorter but slower): the whole trip is repriced and the difference decides.
            point = { distance: ed, duration: et };
            fixed = undefined;
            triggerCode = triggerCodeOf({ distance: activeUp.distance || activeDown.distance, duration: activeUp.duration || activeDown.duration }, distanceKnown);
        }
        else {
            const sense = activeUp !== undefined ? 1 : -1;
            const d = sense === 1 ? up : down;
            const fired = (activeUp ?? activeDown);
            const charged = (q, e, on, bar) => {
                if (d.charge === 'FULL_DIFFERENCE')
                    return e;
                if (!on)
                    return q;
                if (d.charge === 'TRIGGERED_ONLY')
                    return e;
                return Math.round(q + sense * Math.max(0, moved(q, e, sense) - tolerance(q, bar.basisPoints, bar.absolute)));
            };
            point = { distance: charged(qd, ed, fired.distance, d.distance), duration: charged(qt, et, fired.duration, d.duration) };
            fixed = sense === 1 ? 'UP' : 'DOWN';
            triggerCode = triggerCodeOf(fired, distanceKnown);
        }
    }
    if (quoted <= 0n)
        return keep('FINAL_FARE_AS_QUOTED', triggerCode, { triggered });
    if (priceAt === undefined)
        return keep('FINAL_FARE_PRICING_UNAVAILABLE', triggerCode, { triggered });
    let delta;
    try {
        delta = priceAt(point.distance, point.duration) - priceAt(qd, qt);
    }
    catch {
        return keep('FINAL_FARE_PRICING_UNAVAILABLE', triggerCode, { triggered });
    }
    const priced = { triggered, priced: true };
    if (delta === 0n)
        return keep('FINAL_FARE_AS_QUOTED', triggerCode, priced);
    const goesUp = delta > 0n;
    // A fixed direction never moves the other way (an upward trigger never lowers the fare).
    if (fixed !== undefined && (fixed === 'UP') !== goesUp)
        return keep('FINAL_FARE_AS_QUOTED', triggerCode, priced);
    if (goesUp ? !rule.revise.up : !rule.revise.down)
        return keep('FINAL_FARE_DIRECTION_DISABLED', triggerCode, priced);
    const raw = quoted + delta;
    let finalMinor = raw;
    let reasonCode = goesUp ? 'FINAL_FARE_REVISED_UP' : 'FINAL_FARE_REVISED_DOWN';
    let capCode;
    if (goesUp) {
        const byPercent = quoted + bp(quoted, rule.capBasisPoints);
        const byAmount = rule.revise.maxIncrease !== undefined ? quoted + BigInt(rule.revise.maxIncrease.amountMinor) : undefined;
        const ceiling = byAmount !== undefined && byAmount < byPercent ? byAmount : byPercent;
        if (raw > ceiling) {
            finalMinor = ceiling;
            reasonCode = 'FINAL_FARE_REVISED_UP_CAPPED';
            capCode = byAmount !== undefined && byAmount < byPercent ? 'FINAL_FARE_CAP_AMOUNT' : 'FINAL_FARE_CAP_PERCENT';
        }
    }
    else {
        const byPercentRaw = quoted - bp(quoted, rule.floorBasisPoints);
        const byPercent = byPercentRaw > 0n ? byPercentRaw : 0n;
        const byAmountRaw = rule.revise.maxDecrease !== undefined ? quoted - BigInt(rule.revise.maxDecrease.amountMinor) : undefined;
        const byAmount = byAmountRaw !== undefined ? (byAmountRaw > 0n ? byAmountRaw : 0n) : undefined;
        const floor = byAmount !== undefined && byAmount > byPercent ? byAmount : byPercent;
        if (raw < floor) {
            finalMinor = floor;
            reasonCode = 'FINAL_FARE_REVISED_DOWN_FLOORED';
            capCode = byAmount !== undefined && byAmount > byPercent ? 'FINAL_FARE_FLOOR_AMOUNT' : 'FINAL_FARE_FLOOR_PERCENT';
        }
    }
    if (finalMinor === quoted)
        return keep(goesUp ? 'FINAL_FARE_REVISED_UP_CAPPED' : 'FINAL_FARE_REVISED_DOWN_FLOORED', triggerCode, { ...priced, ...(capCode !== undefined ? { capCode } : {}) });
    return { finalMinor, direction: goesUp ? 'UP' : 'DOWN', reasonCode, triggerCode, triggered, priced: true, ...(capCode !== undefined ? { capCode } : {}) };
}
export function simulateFinalFareRevision(rule, example) {
    const rate = (distanceMeters, durationSeconds) => BigInt(Math.round((distanceMeters * example.perKilometerMinor) / 1_000 + (durationSeconds * example.perMinuteMinor) / 60));
    return reviseRoute(rule, {
        quotedMinor: BigInt(Math.max(0, Math.round(example.quotedMinor))),
        quotedDistanceMeters: example.quotedDistanceMeters,
        quotedDurationSeconds: example.quotedDurationSeconds,
        ...(example.distanceEvidenced ? { executedDistanceMeters: example.executedDistanceMeters } : {}),
        executedDurationSeconds: example.executedDurationSeconds,
    }, rate);
}
/** What a completed ride charges: its final fare when one was decided, else its quote (rides completed before D438). */
export function chargeableFare(ride) {
    // D465b: a ride completed without a decided final fare (no governed seam) charges its agreed price in force, else its quote.
    return ride.finalFare?.final ?? agreedPriceInForce(ride)?.total ?? ride.quote.total;
}
//# sourceMappingURL=final-fare.js.map